import React, { useMemo, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import type { AthleteSummary, CalendarSummary, DataFreshness, DayData, SourceKey } from './types';
import { Avatar } from './Avatar';
import { SOURCE_LOGO } from './visual';
import { fmtKm } from './utils/format';

interface Props {
  athlete: AthleteSummary | null;
  summary: CalendarSummary | null;
  freshness: DataFreshness | null;
  /** Per-day payload keyed by ISO date (YYYY-MM-DD) for tooltip timeline. */
  days?: Record<string, DayData> | null;
  /** ISO date — inclusive start of the range covered by `days`. */
  fromDate?: string;
  /** ISO date — exclusive end (matches calendar mappers / month-end convention). */
  toDate?: string;
  /** Per-brand filter toggle state (moved from CalendarToolbar). */
  enabledSources?: SourceKey[];
  onToggleSource?: (s: SourceKey) => void;
}

type Tier = 'green' | 'amber' | 'red' | 'none';

const TIER_COLORS: Record<Tier, string> = {
  green: 'var(--alert-success-text)',
  amber: 'var(--alert-warning-text)',
  red:   'var(--alert-error-text)',
  none:  'var(--card-border)',
};

function tierForScore(score: number | null | undefined): Tier {
  if (score == null) return 'none';
  if (score >= 80) return 'green';
  if (score >= 60) return 'amber';
  return 'red';
}

function enumerateDates(from: string, toInclusive: string): string[] {
  const out: string[] = [];
  const start = new Date(from + 'T00:00:00Z');
  const end   = new Date(toInclusive + 'T00:00:00Z');
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function longestRun(series: Tier[], target: Tier): number {
  let best = 0, cur = 0;
  for (const t of series) {
    if (t === target) { cur++; best = Math.max(best, cur); }
    else cur = 0;
  }
  return best;
}

/** Per-brand freshness rows. Garmin/Whoop merge backend dedicated freshness
 *  (TerraDailyRecord / SleepData+RecoveryData) with terra_activity max-ts;
 *  Coros/Suunto/Oura are activity-only (no dedicated backend row). */
const BRAND_ORDER: SourceKey[] = ['garmin', 'whoop', 'catapult', 'hawkin', 'coros', 'suunto', 'oura'];

type ActivitySource = 'garmin' | 'whoop' | 'coros' | 'suunto' | 'oura';

function maxIso(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

/** Walk `days` and find newest terra_activity start_time per brand. */
function activityFreshnessByBrand(
  days: Record<string, DayData> | null | undefined,
): Partial<Record<ActivitySource, string>> {
  const out: Partial<Record<ActivitySource, string>> = {};
  if (!days) return out;
  for (const day of Object.values(days)) {
    for (const item of day.training ?? []) {
      if (item.kind !== 'terra_activity') continue;
      const cur = out[item.source];
      if (!cur || item.start_time > cur) out[item.source] = item.start_time;
    }
  }
  return out;
}

function relativeAge(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.max(0, Math.round((now - then) / 60_000));
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 48) return `${diffHr}h`;
  return `${Math.round(diffHr / 24)}d`;
}

function fmtSleepHours(min: number | null | undefined): string | null {
  if (min == null) return null;
  return `${(min / 60).toFixed(1)}h`;
}

interface TileProps {
  label: string;
  value: string | null;
  hint?: React.ReactNode;
  popoverTitle?: string;
  popoverBody?: React.ReactNode;
}

function Tile({ label, value, hint, popoverTitle, popoverBody }: TileProps) {
  const [open, setOpen] = useState(false);
  const showPopover = open && !!popoverBody;
  const tooltipId = `tile-tooltip-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div
      className={
        'relative flex flex-col items-start gap-0.5 px-3 py-2 border border-[var(--card-border)] min-w-[88px] ' +
        (popoverBody ? 'cursor-help' : '')
      }
      tabIndex={popoverBody ? 0 : undefined}
      aria-describedby={showPopover ? tooltipId : undefined}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span className="text-[10px] uppercase tracking-wide cgm-text-muted">{label}</span>
      <span className="text-base font-semibold cgm-text-primary">
        {value || <span className="cgm-text-muted">N/A</span>}
      </span>
      {hint}

      {showPopover && (
        <div
          id={tooltipId}
          role="tooltip"
          data-testid={tooltipId}
          className="absolute left-0 top-full mt-1 z-50 w-[300px] p-3 cgm-bg-primary border border-[var(--card-border)] shadow-lg"
        >
          {popoverTitle && (
            <div className="text-[10px] uppercase tracking-wide cgm-text-muted mb-1">
              {popoverTitle}
            </div>
          )}
          <div className="text-[11px] cgm-text-secondary leading-relaxed">
            {popoverBody}
          </div>
        </div>
      )}
    </div>
  );
}

interface ReadinessTileProps {
  summary: CalendarSummary | null;
  days: Record<string, DayData> | null | undefined;
  fromDate: string | undefined;
  toDate: string | undefined;
  tierBar: React.ReactNode;
}

function ReadinessTile({ summary, days, fromDate, toDate, tierBar }: ReadinessTileProps) {
  const [open, setOpen] = useState(false);

  const series = useMemo<{ date: string; tier: Tier; score: number | null }[]>(() => {
    if (!days || !fromDate || !toDate) return [];
    return enumerateDates(fromDate, toDate).map((date) => {
      const score = days[date]?.recovery?.readiness?.score ?? null;
      return { date, tier: tierForScore(score), score };
    });
  }, [days, fromDate, toDate]);

  const stats = useMemo(() => {
    const tiers = series.map((s) => s.tier);
    const observed = series.filter((s) => s.tier !== 'none');
    const scores = observed.map((s) => s.score!).filter((n) => n != null);
    const avg = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
    return {
      total: series.length,
      observed: observed.length,
      avg,
      bestGreenStreak: longestRun(tiers, 'green'),
      worstRedStreak: longestRun(tiers, 'red'),
      green: observed.filter((s) => s.tier === 'green').length,
      amber: observed.filter((s) => s.tier === 'amber').length,
      red:   observed.filter((s) => s.tier === 'red').length,
    };
  }, [series]);

  const value = summary?.avg_readiness != null ? `${summary.avg_readiness}` : null;
  const showTooltip = open && series.length > 0;

  return (
    <div
      className="relative flex flex-col items-start gap-0.5 px-3 py-2 border border-[var(--card-border)] min-w-[88px] cursor-help"
      tabIndex={0}
      aria-describedby={showTooltip ? 'readiness-tooltip' : undefined}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <span className="text-[10px] uppercase tracking-wide cgm-text-muted">Readiness</span>
      <span className="text-base font-semibold cgm-text-primary">
        {value || <span className="cgm-text-muted">N/A</span>}
      </span>
      {tierBar}

      {showTooltip && (
        <div
          id="readiness-tooltip"
          role="tooltip"
          data-testid="readiness-tooltip"
          className="absolute left-0 top-full mt-1 z-50 w-[320px] p-3 cgm-bg-primary border border-[var(--card-border)] shadow-lg"
        >
          <div className="text-[10px] uppercase tracking-wide cgm-text-muted mb-1">
            Readiness over time
          </div>
          <div className="text-[11px] cgm-text-muted mb-2">
            {fmtShortDate(series[0].date)} – {fmtShortDate(series[series.length - 1].date)}
          </div>

          <div
            data-testid="readiness-timeline"
            className="flex h-6 w-full overflow-hidden border border-[var(--card-border)]"
          >
            {series.map((s) => (
              <div
                key={s.date}
                data-tier={s.tier}
                title={`${fmtShortDate(s.date)} · ${s.score != null ? s.score : '—'}`}
                style={{ flex: '1 1 0', backgroundColor: TIER_COLORS[s.tier] }}
              />
            ))}
          </div>

          <div className="flex justify-between mt-1 text-[10px] cgm-text-muted">
            <span>{fmtShortDate(series[0].date)}</span>
            {series.length > 6 && (
              <span>{fmtShortDate(series[Math.floor(series.length / 2)].date)}</span>
            )}
            <span>{fmtShortDate(series[series.length - 1].date)}</span>
          </div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-[11px]">
            <span className="cgm-text-muted">Avg readiness</span>
            <span className="cgm-text-primary text-right">{stats.avg ?? '—'}</span>
            <span className="cgm-text-muted">Days observed</span>
            <span className="cgm-text-primary text-right">{stats.observed} / {stats.total}</span>
            <span className="cgm-text-muted">Longest green streak</span>
            <span className="cgm-text-primary text-right">{stats.bestGreenStreak}d</span>
            <span className="cgm-text-muted">Longest red streak</span>
            <span className="cgm-text-primary text-right">{stats.worstRedStreak}d</span>
          </div>

          <div className="flex items-center gap-3 mt-2 text-[10px] cgm-text-muted">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2" style={{ backgroundColor: TIER_COLORS.green }} />
              {stats.green}g
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2" style={{ backgroundColor: TIER_COLORS.amber }} />
              {stats.amber}o
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2" style={{ backgroundColor: TIER_COLORS.red }} />
              {stats.red}r
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 border border-[var(--card-border)]" />
              {stats.total - stats.observed} missing
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export function AthleteSummaryStrip({
  athlete, summary, freshness, days, fromDate, toDate,
  enabledSources = [], onToggleSource,
}: Props) {
  const { isDark } = useTheme();
  const total = summary
    ? Math.max(1, summary.red_days + summary.amber_days + summary.green_days)
    : 1;
  const pct = (n: number) => `${((n / total) * 100).toFixed(2)}%`;

  const tierTooltip = summary
    ? `Days in this date range by readiness tier — ${summary.green_days} green (≥80), `
      + `${summary.amber_days} orange (60–79), ${summary.red_days} red (<60).`
    : undefined;

  const tierBar = summary && (summary.red_days + summary.amber_days + summary.green_days) > 0 ? (
    <div className="mt-1 flex flex-col gap-0.5 w-full" title={tierTooltip}>
      <div data-testid="tier-bar" className="flex h-1 w-full overflow-hidden">
        <div data-tier="green" style={{ width: pct(summary.green_days), backgroundColor: 'var(--alert-success-text)' }} />
        <div data-tier="amber" style={{ width: pct(summary.amber_days), backgroundColor: 'var(--alert-warning-text)' }} />
        <div data-tier="red"   style={{ width: pct(summary.red_days),   backgroundColor: 'var(--alert-error-text)' }} />
      </div>
      <span className="text-[10px] cgm-text-muted">
        {summary.green_days}g · {summary.amber_days}o · {summary.red_days}r
      </span>
    </div>
  ) : null;

  return (
    <div className="flex items-stretch gap-4 px-4 py-3 border-b border-[var(--card-border)]">
      {athlete && (
        <div className="flex items-center gap-3 pr-3 border-r border-[var(--card-border)]">
          <Avatar fullName={athlete.full_name} size={40} />
          <div className="flex flex-col">
            <span className="text-sm font-semibold cgm-text-primary">{athlete.full_name}</span>
            <span className="text-xs cgm-text-muted">
              {[athlete.position, athlete.sport, athlete.jersey_number ? `#${athlete.jersey_number}` : null]
                .filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>
      )}

      <div className="flex items-stretch gap-2">
        <ReadinessTile
          summary={summary}
          days={days}
          fromDate={fromDate}
          toDate={toDate}
          tierBar={tierBar}
        />
        <Tile
          label="Sessions"
          value={summary ? `${summary.sessions_count}` : null}
          popoverTitle="What counts as a session"
          popoverBody={
            <>
              <p className="mb-2">
                Discrete training sessions captured in this view's date range.
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Catapult sessions (GPS / IMU workouts)</li>
                <li>Terra workouts (runs, rides, lifts via Garmin / Coros / Suunto / Oura / Whoop)</li>
              </ul>
              <p className="mt-2 cgm-text-muted">
                Excludes: Garmin daily aggregates (rolled into Distance) and Hawkin tests (Assessments).
              </p>
            </>
          }
        />
        <Tile
          label="Distance"
          value={summary ? (fmtKm(summary.total_distance_m) ?? '0 mi') : null}
          popoverTitle="Total distance"
          popoverBody={
            <>
              <p className="mb-2">
                Sum of distance covered across every source in this view's date range — displayed in imperial miles.
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Garmin daily steps + walks</li>
                <li>Catapult session GPS</li>
                <li>Terra workout distance (run / bike, when reported)</li>
              </ul>
            </>
          }
        />
        <Tile
          label="Avg Sleep"
          value={fmtSleepHours(summary?.avg_sleep_minutes)}
          popoverTitle="Average sleep duration"
          popoverBody={
            <>
              <p className="mb-2">
                Mean total-in-bed duration across nights with sleep data in this view's date range.
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Source: Whoop sleep records only</li>
                <li>Garmin sleep is Phase 2 — not yet included</li>
                <li>Nights without recorded sleep are excluded from the average</li>
              </ul>
            </>
          }
        />
        <Tile
          label="Assessments"
          value={summary ? `${summary.assessments_count}` : null}
          popoverTitle="Force-plate tests"
          popoverBody={
            <>
              <p className="mb-2">
                Hawkin Dynamics force-plate tests in this view's date range.
              </p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Countermovement Jump, Squat Jump, Drop Jump, Isometric Test</li>
                <li>Click any day on the calendar to see jump height, peak force, RSI, and L/R asymmetry</li>
              </ul>
            </>
          }
        />
      </div>

      <div className="flex-1" />

      <div className="flex items-stretch gap-1.5" data-testid="source-chip-row">
        {(() => {
          const activityFreshness = activityFreshnessByBrand(days);
          const enabledSet = new Set(enabledSources);
          return BRAND_ORDER.map((brand) => {
            const dedicated =
              brand === 'garmin'   ? freshness?.garmin   ?? null :
              brand === 'whoop'    ? freshness?.whoop    ?? null :
              brand === 'catapult' ? freshness?.catapult ?? null :
              brand === 'hawkin'   ? freshness?.hawkin   ?? null :
              null;
            const activity = (brand === 'garmin' || brand === 'whoop' || brand === 'coros'
                              || brand === 'suunto' || brand === 'oura')
              ? activityFreshness[brand] ?? null
              : null;
            const iso = maxIso(dedicated, activity);
            const on = enabledSet.has(brand);
            const logo = SOURCE_LOGO[brand];
            return (
              <button
                key={brand}
                type="button"
                data-testid={`source-chip-${brand}`}
                aria-pressed={on}
                onClick={() => onToggleSource?.(brand)}
                title={`${brand} · ${iso ? relativeAge(iso) : 'no data'}`}
                className={
                  'flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 border border-[var(--card-border)] min-w-[48px] ' +
                  (on ? '' : 'opacity-30 grayscale')
                }
              >
                <img
                  src={isDark ? logo.dark : logo.light}
                  alt={`${brand} logo`}
                  className="w-4 h-4 object-contain"
                />
                <span className="text-[10px] cgm-text-muted leading-none">
                  {iso ? relativeAge(iso) : '—'}
                </span>
              </button>
            );
          });
        })()}
      </div>
    </div>
  );
}
